import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { CheckCircleIcon, XCircleIcon, WarningIcon, ClockIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";
import { ProjectSubmissionStatus, ProjectVettingStatus } from "../../../graphql/generated/graphql";
import { CustomHtmlContent } from "../CustomHTMLContent/CustomHTMLContent";

const ProjectVettingState = ({
  submission,
  submissionRefetch,
  onAccept,
  onRevise,
  resetVettingState,
  setResetVettingState,
  projectFramework = null,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [stableSubmission, setStableSubmission] = useState(null);

  useEffect(() => {
    if (resetVettingState) {
      setStableSubmission(null);
      setRetryCount(0);
      setResetVettingState(false);
    }
  }, [resetVettingState]);

  const pollInterval = useRef(null);

  const startManualPolling = () => {
    if (!pollInterval.current) {
      pollInterval.current = setInterval(async () => {
        await submissionRefetch();
      }, 3000);
    }
  };

  useEffect(() => {
    if (!submission) {
      return;
    }

    const inProgressVetting =
      submission.vettingStatus === ProjectVettingStatus.NotStarted ||
      submission.vettingStatus === ProjectVettingStatus.InProgress;

    // Update stable submission if we have better data or no stable data yet
    if (!inProgressVetting) {
      setStableSubmission(submission);

      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    } else {
      startManualPolling();
    }
  }, [submission]);

  // Manual retry function
  const handleRetry = async () => {
    setRetryCount((prev) => prev + 1);
    await submissionRefetch();
  };

  // Loading state - only show loading if we haven't shown results yet AND don't have vetting data
  if (!stableSubmission) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ClockIcon size={48} color="#667085" />
          <CustomText
            style={styles.loadingText}
            text="AI is reviewing your proposal..."
            useTranslationText={false}
            aria-level="2"
            role="heading"
          />
          <CustomText
            style={styles.loadingSubText}
            text="This usually takes 10-30 seconds"
            useTranslationText={false}
          />

          {/* Show retry after some time */}
          {retryCount > 0 && (
            <CustomButton
              text="Check Again"
              size="m"
              styleType="secondary"
              onPress={handleRetry}
              style={styles.checkAgainButton}
              icon={<ArrowClockwiseIcon size={16} />}
            />
          )}
        </View>
      </View>
    );
  }

  // Helper functions for vetting results
  const getStatusIcon = () => {
    const status = stableSubmission.vettingStatus || stableSubmission.status;
    switch (status) {
      case ProjectSubmissionStatus.Approved:
        return (
          <CheckCircleIcon
            size={32}
            color="#12B76A"
            weight="fill"
            aria-label="Project approved icon"
            role="img"
            alt="Project approved icon"
          />
        );
      case ProjectSubmissionStatus.NeedsRevision:
        return (
          <WarningIcon
            size={32}
            color="#F79009"
            weight="fill"
            aria-label="Project needs revision icon"
            role="img"
            alt="Project needs revision icon"
          />
        );
      case ProjectSubmissionStatus.Rejected:
        return (
          <XCircleIcon
            size={32}
            color="#F04438"
            weight="fill"
            aria-label="Project rejected icon"
            role="img"
            alt="Project rejected icon"
          />
        );
      default:
        return (
          <ClockIcon
            size={32}
            color="#667085"
            weight="fill"
            aria-label="Project under review icon"
            role="img"
            alt="Project under review icon"
          />
        );
    }
  };

  const getStatusColor = () => {
    const status = stableSubmission.vettingStatus || stableSubmission.status;
    switch (status) {
      case ProjectSubmissionStatus.Approved:
        return "#12B76A";
      case ProjectSubmissionStatus.NeedsRevision:
        return "#F79009";
      case ProjectSubmissionStatus.Rejected:
        return "#F04438";
      default:
        return "#667085";
    }
  };

  const getStatusText = () => {
    const status = stableSubmission.vettingStatus || stableSubmission.status;
    switch (status) {
      case ProjectSubmissionStatus.Approved:
        return "Project Approved";
      case ProjectSubmissionStatus.NeedsRevision:
      case ProjectSubmissionStatus.Rejected:
        return "Changes Requested";
      default:
        return "Under Review";
    }
  };

  const getStatusSubText = () => {
    const status = stableSubmission.vettingStatus || stableSubmission.status;
    switch (status) {
      case ProjectSubmissionStatus.Approved:
        return "Your proposal is approved. Next we’ll generate your task plan so you can start. You can continue or make edits first.";
      case ProjectSubmissionStatus.NeedsRevision:
      case ProjectSubmissionStatus.Rejected:
        return "Your proposal needs updates to meet the project requirements. Review the notes, make edits, and resubmit for approval.";
      default:
        return "Under Review";
    }
  };

  const getComplianceColor = (score) => {
    if (score >= 80) return "#12B76A";
    if (score >= 60) return "#F79009";
    return "#F04438";
  };

  // Parse AI assessment
  let aiAssessment = null;
  try {
    if (stableSubmission.aiAssessment && typeof stableSubmission.aiAssessment === "string") {
      aiAssessment = JSON.parse(stableSubmission.aiAssessment);
    }
  } catch (error) {
    console.log("Error parsing AI assessment:", error);
  }

  const feedbackText =
    aiAssessment?.encouragement ||
    aiAssessment?.alignmentAnalysis ||
    stableSubmission.feedback ||
    "AI analysis completed. Check your score and feedback below.";

  const strengths = aiAssessment?.strengths || [];
  const quickWins = aiAssessment?.quickWins || stableSubmission.suggestedImprovements || [];
  const majorConcerns = aiAssessment?.majorConcerns || [];

  return (
    <View style={styles.container}>
      {projectFramework && (
        <View style={styles.projectFramework}>
          <ScrollView
            style={styles.formContainer}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
            tabIndex={0}>
            <CustomText
              style={styles.sectionTitle}
              text="Project Framework"
              useTranslationText={false}
              aria-level="2"
              role="heading"
            />
            <View style={styles.frameworkContainer}>
              <View style={styles.frameworkItem}>
                <CustomText
                  style={styles.frameworkLabel}
                  text="Project:"
                  useTranslationText={false}
                  aria-level="3"
                  role="heading"
                />
                <CustomText
                  style={styles.frameworkText}
                  text={projectFramework.name || "Untitled Project"}
                  useTranslationText={false}
                />
              </View>

              {projectFramework.description && (
                <View style={styles.frameworkItem}>
                  <CustomText
                    style={styles.frameworkLabel}
                    text="Description:"
                    useTranslationText={false}
                    aria-level="3"
                    role="heading"
                  />
                  <CustomHtmlContent htmlString={projectFramework.description} />
                </View>
              )}

              {projectFramework.scopeDocument && (
                <View style={styles.frameworkItem}>
                  <CustomText
                    style={styles.frameworkLabel}
                    text="Scope & Requirements:"
                    useTranslationText={false}
                    aria-level="3"
                    role="heading"
                  />
                  <CustomHtmlContent htmlString={projectFramework.scopeDocument} />
                </View>
              )}

              {projectFramework.deliverableTypes && projectFramework.deliverableTypes.length > 0 && (
                <View style={styles.frameworkItem}>
                  <CustomText
                    style={styles.frameworkLabel}
                    text="Deliverable Types:"
                    useTranslationText={false}
                    aria-level="3"
                    role="heading"
                  />
                  {projectFramework.deliverableTypes.map((constraint, index) => (
                    <CustomText
                      key={index}
                      style={styles.frameworkListItem}
                      text={`• ${constraint}`}
                      useTranslationText={false}
                    />
                  ))}
                </View>
              )}

              {projectFramework.assessmentCriteria && projectFramework.assessmentCriteria.length > 0 && (
                <View style={styles.frameworkItem}>
                  <CustomText
                    style={styles.frameworkLabel}
                    text="Assessment Criteria:"
                    useTranslationText={false}
                    aria-level="3"
                    role="heading"
                  />
                  {projectFramework.assessmentCriteria.map((criteria, index) => (
                    <CustomText
                      key={index}
                      style={styles.frameworkListItem}
                      text={`• ${criteria}`}
                      useTranslationText={false}
                    />
                  ))}
                </View>
              )}

              {projectFramework.expectedOutcomes && projectFramework.expectedOutcomes.length > 0 && (
                <View style={styles.frameworkItem}>
                  <CustomText
                    style={styles.frameworkLabel}
                    text="Learning Objectives:"
                    useTranslationText={false}
                    aria-level="3"
                    role="heading"
                  />
                  {projectFramework.expectedOutcomes.map((outcome, index) => (
                    <CustomText
                      key={index}
                      style={styles.frameworkListItem}
                      text={`• ${outcome}`}
                      useTranslationText={false}
                    />
                  ))}
                </View>
              )}

              {projectFramework.dueDate && (
                <View style={styles.frameworkItem}>
                  <CustomText style={styles.frameworkLabel} text="Due Date:" aria-level="3" role="heading" />
                  <CustomText
                    style={styles.frameworkText}
                    text={new Date(projectFramework.dueDate).toLocaleDateString()}
                    useTranslationText={false}
                  />
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      )}

      <View style={styles.evaluationSection}>
        <ScrollView style={styles.formContainer} showsVerticalScrollIndicator={true} nestedScrollEnabled={true}>
          <View style={styles.header}>
            <View style={styles.statusContainer}>
              {getStatusIcon()}
              <View style={styles.statusTextContainer}>
                <CustomText
                  style={[styles.statusText, { color: getStatusColor() }]}
                  text={getStatusText()}
                  useTranslationText={false}
                  aria-level="2"
                  role="heading"
                />
              </View>
            </View>
            <CustomText style={styles.subStatusText} text={getStatusSubText()} useTranslationText={false} />
          </View>
          {/* Compliance Score */}
          <View style={styles.section}>
            <CustomText
              style={styles.sectionTitle}
              text="Compliance Score"
              useTranslationText={false}
              aria-level="3"
              role="heading"
            />
            <View style={styles.scoreContainer}>
              <View style={styles.scoreCircle}>
                <CustomText
                  style={[styles.scoreText, { color: getComplianceColor(stableSubmission.complianceScore) }]}
                  text={`${stableSubmission.complianceScore}/100`}
                  useTranslationText={false}
                />
              </View>
              <View style={styles.scoreDetails}>
                <CustomText
                  style={styles.scoreLabel}
                  text="Overall Alignment"
                  useTranslationText={false}
                  aria-level="3"
                  role="heading"
                />
                <CustomText
                  style={styles.scoreDescription}
                  text="How well your project aligns with course objectives and academic standards"
                  useTranslationText={false}
                />
              </View>
            </View>
          </View>

          {/* AI Feedback */}
          <View style={styles.section}>
            <CustomText
              style={styles.sectionTitle}
              text="AI Feedback"
              useTranslationText={false}
              aria-level="3"
              role="heading"
            />
            <View style={styles.feedbackContainer}>
              <CustomText style={styles.feedbackText} text={feedbackText} useTranslationText={false} />
            </View>
          </View>

          {/* Strengths */}
          {strengths.length > 0 && (
            <View style={styles.section}>
              <CustomText
                style={styles.sectionTitle}
                text="What You're Doing Well"
                useTranslationText={false}
                aria-level="3"
                role="heading"
              />
              {strengths.map((strength, index) => (
                <View key={index} style={styles.suggestionItem}>
                  <View style={[styles.suggestionBullet, { backgroundColor: "#12B76A" }]} />
                  <CustomText style={styles.suggestionText} text={strength} useTranslationText={false} />
                </View>
              ))}
            </View>
          )}

          {/* Quick Wins */}
          {quickWins.length > 0 && (
            <View style={styles.section}>
              <CustomText
                style={styles.sectionTitle}
                text="Quick Improvements"
                useTranslationText={false}
                aria-level="3"
                role="heading"
              />
              {quickWins.map((suggestion, index) => (
                <View key={index} style={styles.suggestionItem}>
                  <View style={styles.suggestionBullet} />
                  <CustomText style={styles.suggestionText} text={suggestion} useTranslationText={false} />
                </View>
              ))}
            </View>
          )}

          {/* Major Concerns */}
          {majorConcerns.length > 0 && (
            <View style={styles.section}>
              <CustomText
                style={styles.sectionTitle}
                text="Areas Needing Attention"
                useTranslationText={false}
                aria-level="3"
                role="heading"
              />
              {majorConcerns.map((concern, index) => (
                <View key={index} style={styles.suggestionItem}>
                  <View style={[styles.suggestionBullet, { backgroundColor: "#F04438" }]} />
                  <CustomText style={styles.suggestionText} text={concern} useTranslationText={false} />
                </View>
              ))}
            </View>
          )}

          {/* {renderDebugInfo()} */}

          <View style={styles.footer}>
            {(stableSubmission.status === ProjectSubmissionStatus.Approved ||
              stableSubmission.vettingStatus === ProjectVettingStatus.Approved) && (
              <CustomButton
                text="Accept & Continue"
                size="m"
                styleType="primary"
                onPress={onAccept}
                aria-label="Accept & Continue button"
                accessibilityLabel="Accept & Continue button"
              />
            )}
            <CustomButton
              text="Edit Proposal"
              size="m"
              styleType={
                stableSubmission.status === ProjectSubmissionStatus.Approved ||
                stableSubmission.vettingStatus === ProjectVettingStatus.Approved
                  ? "primaryLight"
                  : "primary"
              }
              onPress={onRevise}
              style={styles.reviseButton}
            />
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "row",
    backgroundColor: "#ffffff",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    overflow: "hidden",
  },
  header: {
    marginBottom: 21,
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  statusTextContainer: {
    marginLeft: 16,
  },
  statusText: {
    fontSize: 24,
    fontWeight: "600",
    marginBottom: 4,
  },
  subStatusText: {
    fontSize: 16,
    color: "#667085",
    fontWeight: "400",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 64,
  },
  loadingText: {
    fontSize: 18,
    color: "#667085",
    marginTop: 16,
  },
  loadingSubText: {
    fontSize: 14,
    color: "#98A2B3",
    marginTop: 8,
  },
  checkAgainButton: {
    marginTop: 24,
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#101828",
    marginBottom: 16,
  },
  scoreContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  scoreCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 3,
    borderColor: "#EAECF0",
    justifyContent: "center",
    alignItems: "center",
  },
  scoreText: {
    fontSize: 18,
    fontWeight: "700",
  },
  scoreDetails: {
    marginLeft: 20,
    flex: 1,
  },
  scoreLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#101828",
    marginBottom: 4,
  },
  scoreDescription: {
    fontSize: 14,
    color: "#667085",
  },
  feedbackContainer: {
    backgroundColor: "#F9FAFB",
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#3E68FE",
  },
  feedbackText: {
    fontSize: 14,
    color: "#101828",
    lineHeight: 20,
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  suggestionBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#F79009",
    marginTop: 7,
    marginRight: 12,
  },
  suggestionText: {
    fontSize: 14,
    color: "#101828",
    flex: 1,
    lineHeight: 20,
  },
  footer: {
    flexDirection: "row",
    gap: 8,
  },
  reviseButton: {
    fontWeight: 700,
    marginRight: 16,
    borderWidth: 1,
    borderColor: "#05060F1A",
  },
  frameworkContainer: {
    backgroundColor: "#F8FAFC",
    padding: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  frameworkItem: {
    marginBottom: 16,
  },
  frameworkLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  frameworkText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1E293B",
    lineHeight: 22,
  },
  frameworkListItem: {
    fontSize: 14,
    color: "#475569",
    lineHeight: 20,
    marginBottom: 4,
  },
  projectFramework: {
    height: "100%",
    width: 360,
    borderRightWidth: 1,
    borderRightColor: "#EAECF0",
  },
  evaluationSection: {
    height: "100%",
    flex: 1,
  },
  formContainer: {
    flex: 1,
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
});

export default ProjectVettingState;
